import {
  ChartLine,
  FileText,
  FlaskConical,
  HardDrive,
  ImageIcon,
  Landmark,
  LayoutDashboard,
  MessageCircle,
  Newspaper,
  Package,
  ReceiptText,
  Search,
  School,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';

import type { RoleName } from '@/types/api';

import type { NavItem, NavSection } from './navigation';

/**
 * Peran yang melihat menu konsol, bukan menu lembaga.
 *
 * Cerminan PLATFORM_ROLES di PlatformScopeGuard sisi server. Yang membedakan
 * apa yang benar-benar terbuka bagi masing-masing bukan daftar ini melainkan
 * `permission` pada tiap butir menu di bawah — Marketing dan Direktur sama-sama
 * sampai di sini, lalu berpisah di situ.
 */
export const PLATFORM_ROLES: readonly RoleName[] = ['SUPER_ADMIN', 'DIREKTUR', 'MARKETING'];

/**
 * Menu Super Admin — konsol platform, BUKAN dashboard operasional tenant.
 *
 * Dipakai terpisah dari NAVIGATION (config/navigation.ts): Super Admin
 * tidak punya tenant sendiri, jadi menu Master Data/Akademik/Keuangan dst
 * tidak relevan untuknya. Lihat Sidebar.tsx untuk logika pemilihannya.
 */
/**
 * Alamat menu Kelola Tenant. Dipakai sidebar untuk menempelkan penanda
 * pendaftaran yang menunggu persetujuan — dijadikan konstanta agar penanda itu
 * tidak diam-diam berhenti muncul ketika alamatnya suatu saat diubah di sini.
 */
export const PLATFORM_TENANTS_HREF = '/admin/tenants';

/**
 * Alamat menu Bukti Pembayaran. Dipakai sidebar untuk menempelkan penanda
 * jumlah bukti yang menunggu diperiksa — dijadikan konstanta dengan alasan
 * yang sama seperti PLATFORM_TENANTS_HREF di atas.
 */
export const PLATFORM_SUBSCRIPTION_CONFIRMATIONS_HREF = '/admin/langganan/bukti';

export const PLATFORM_NAVIGATION: NavSection[] = [
  {
    label: 'Platform',
    items: [
      {
        // Ringkasannya memuat pemasukan dan tunggakan, jadi ia berdiri di
        // balik izinnya sendiri — bukan sesuatu yang otomatis ikut hanya
        // karena seseorang boleh membuka konsol.
        label: 'Dashboard Platform',
        href: '/admin',
        icon: LayoutDashboard,
        permission: 'platformStats.read',
      },
      {
        // Tepat di bawah Dashboard Platform, bukan di Konten Publik. Yang
        // dihitung di sana memang sebagian besar trafik situs publik, tetapi
        // pertanyaan yang membawa orang ke sana — "seberapa ramai SIPAUD
        // bulan ini" — adalah pertanyaan yang sama yang membawanya ke
        // Dashboard Platform, bukan pertanyaan tentang mengelola konten.
        label: 'Statistik Pengunjung',
        href: '/admin/pengunjung',
        icon: ChartLine,
        permission: 'visitorAnalytics.read',
      },
      {
        // Bersebelahan dengan dua statistik lain: pertanyaannya sejenis —
        // "seberapa sehat SIPAUD" — hanya diukur dari lembaga uji coba.
        label: 'Statistik Uji Coba',
        href: '/admin/uji-coba',
        icon: FlaskConical,
        permission: 'platformStats.read',
      },
      { label: 'Kelola Tenant', href: PLATFORM_TENANTS_HREF, icon: School, permission: 'tenant.read' },
      { label: 'Paket Langganan', href: '/admin/plans', icon: Package, permission: 'plan.read' },
      {
        // Di bawah Paket Langganan: kuota penyimpanan ditetapkan paket, dan
        // di sinilah terlihat apakah janji kuota itu masih muat di server.
        label: 'Penyimpanan',
        href: '/admin/penyimpanan',
        icon: HardDrive,
        permission: 'platformStats.read',
      },
      { label: 'Tagihan Platform', href: '/admin/invoices', icon: FileText, permission: 'billing.read' },
      {
        // Berurutan mengikuti alur uangnya: tagihan terbit, lembaga mengirim
        // bukti, buktinya diperiksa, hasilnya masuk rekap pemasukan. Rekening
        // tujuan ditaruh terakhir karena ia setelan yang diisi sekali, bukan
        // pekerjaan harian.
        label: 'Bukti Pembayaran',
        href: PLATFORM_SUBSCRIPTION_CONFIRMATIONS_HREF,
        icon: ReceiptText,
        permission: 'subscriptionConfirmation.read',
      },
      {
        label: 'Pemasukan Langganan',
        href: '/admin/langganan/pemasukan',
        icon: Wallet,
        permission: 'billing.read',
      },
      {
        label: 'Rekening Langganan',
        href: '/admin/langganan/rekening',
        icon: Landmark,
        permission: 'subscriptionChannel.read',
      },
      {
        // Di dekat Kelola Tenant, bukan di bagian lain: keduanya sama-sama
        // "siapa yang boleh masuk ke mana", dan yang mencari cara mereset
        // sandi seorang staf mencarinya di dekat pengelolaan akun.
        label: 'Akun Platform',
        href: '/admin/akun',
        icon: UserCog,
        permission: 'platformUser.read',
      },
      {
        label: 'Notifikasi WA & Telegram',
        href: '/admin/notifikasi',
        icon: MessageCircle,
        permission: 'platformNotification.read',
      },
      {
        // Ditaruh di Platform, bukan Konten Publik. Sempat di sana karena
        // "yang diatur dibaca orang di luar konsol" — alasan yang rapi di
        // kepala penulisnya dan tidak menolong siapa pun yang mencarinya.
        // Orang mencari setelan WhatsApp di dekat setelan WhatsApp.
        label: 'Grup WhatsApp',
        href: '/admin/komunitas',
        // Bukan MessageCircle: ikon itu sudah dipakai Notifikasi WhatsApp
        // tepat di atasnya, dan dua baris beruntun dengan lambang yang sama
        // membuat keduanya harus dibaca kata demi kata untuk dibedakan.
        icon: Users,
        permission: 'platformCommunity.read',
      },
    ] satisfies NavItem[],
  },
  {
    label: 'Konten Publik',
    items: [
      { label: 'Blog', href: '/admin/blog', icon: Newspaper, permission: 'blog.read' },
      { label: 'SEO Halaman', href: '/admin/seo', icon: Search, permission: 'seoSetting.read' },
      {
        label: 'Logo & Favicon',
        href: '/admin/merek',
        icon: ImageIcon,
        permission: 'platformBranding.read',
      },
    ] satisfies NavItem[],
  },
];
